import { sections, type SectionId } from '@/data/learningTest';
import type { IconName } from '@monorepo/shared-ui';

/**
 * Get icon name for a section type
 * Returns IconName compatible with shared-ui SvgIcon component
 */
export function getSectionIcon(type: SectionId): IconName {
	const icons: Record<SectionId, IconName> = {
		visual: 'eye',
		auditory: 'ear',
		reading: 'book-open',
		kinesthetic: 'hand',
		integrated: 'refresh-cw',
		giftedness: 'star',
		auditoryProcessing: 'music',
		highSensitivity: 'sparkles',
		adhd: 'zap',
	};
	return icons[type] || 'brain';
}

/**
 * Get title for a section
 */
export function getSectionTitle(id: SectionId): string {
	return sections.find((s) => s.id === id)?.title ?? id;
}

/**
 * Get short title for a section (used in badges)
 */
export function getSectionShortTitle(type: SectionId): string {
	const titles: Record<SectionId, string> = {
		visual: 'Visuell',
		auditory: 'Auditiv',
		reading: 'Lesen/Schreiben',
		kinesthetic: 'Kinästhetisch',
		integrated: 'Integriert',
		giftedness: 'Hochbegabung',
		auditoryProcessing: 'Hörverarbeitung',
		highSensitivity: 'Hochsensibilität',
		adhd: 'ADHS',
	};
	return titles[type] || type;
}

/**
 * Get description for a dominant learning style
 */
export function getDominantStyleDescription(type: SectionId, childName: string): string {
	const descriptions: Record<SectionId, string> = {
		visual: `${childName} lernt am besten durch Sehen. Bilder, Diagramme, Farben und visuelle Hilfsmittel helfen beim Verstehen und Merken. Nutzen Sie Mindmaps, bunte Notizen und Lernposter.`,
		auditory: `${childName} lernt am besten durch Hören. Erklärungen, Diskussionen und gesprochene Inhalte sind ideal. Lesen Sie gemeinsam vor und nutzen Sie Lernlieder oder Podcasts.`,
		reading: `${childName} lernt am besten durch Lesen und Schreiben. Texte, Notizen und schriftliche Übungen sind perfekt. Ermutigen Sie zum Mitschreiben und Zusammenfassen.`,
		kinesthetic: `${childName} lernt am besten durch Bewegung und praktisches Tun. Experimente, Bewegung beim Lernen und haptische Materialien sind ideal.`,
		integrated: `${childName} zeigt eine integrierte Lernweise.`,
		giftedness: `${childName} zeigt Anzeichen von Hochbegabung.`,
		auditoryProcessing: `${childName} hat besondere auditive Verarbeitungsmuster.`,
		highSensitivity: `${childName} ist hochsensibel.`,
		adhd: `${childName} zeigt ADHS-Tendenzen.`,
	};
	return descriptions[type] || '';
}

/**
 * Get quick tips for a learning style
 */
export function getQuickTips(type: SectionId): string[] {
	const tips: Record<SectionId, string[]> = {
		visual: [
			'Verwenden Sie bunte Marker und Textmarker beim Lernen',
			'Erstellen Sie gemeinsam Mindmaps und Lernposter',
			'Nutzen Sie Bilder und Diagramme zur Veranschaulichung',
		],
		auditory: [
			'Lesen Sie Lerninhalte laut vor',
			'Nutzen Sie Lernlieder und Reime',
			'Führen Sie Diskussionen über den Lernstoff',
		],
		reading: [
			'Ermutigen Sie zum Mitschreiben während Erklärungen',
			'Lassen Sie Zusammenfassungen schreiben',
			'Nutzen Sie Karteikarten mit Texten',
		],
		kinesthetic: [
			'Erlauben Sie Bewegung beim Lernen',
			'Nutzen Sie praktische Experimente',
			'Verwenden Sie haptische Lernmaterialien',
		],
		integrated: [
			'Kombinieren Sie verschiedene Lernmethoden — visuell, auditiv und praktisch',
			'Wechseln Sie regelmäßig zwischen Lernmethoden, um die Motivation zu erhalten',
			'Multimediale Materialien (Video + Text + Übung) sind besonders effektiv',
		],
		giftedness: [
			'Bieten Sie vertiefte, herausfordernde Aufgaben über den Schulstoff hinaus an',
			'Fördern Sie eigenständige Projekte zu Interessensthemen Ihres Kindes',
			'Suchen Sie nach Begabtenförderung oder Enrichment-Programmen in der Region',
		],
		auditoryProcessing: [
			'Nutzen Sie visuelle Unterstützung (Bilder, Schrift) parallel zu mündlichen Erklärungen',
			'Sprechen Sie in einer ruhigen Umgebung ohne Hintergrundgeräusche',
			'Erlauben Sie mehr Wiederholungen und Rückfragen beim Erklären',
		],
		highSensitivity: [
			'Schaffen Sie eine ruhige, reizarme Lernumgebung mit fester Routine',
			'Planen Sie bewusste Pausen zwischen intensiven Lernphasen ein',
			'Validieren Sie Gefühle und bieten Sie emotionale Sicherheit vor dem Lernen',
		],
		adhd: [
			'Teilen Sie Lerneinheiten in 10–15-Minuten-Blöcke mit kurzen Bewegungspausen',
			'Nutzen Sie klare, schriftliche Aufgabenlisten statt langer mündlicher Anweisungen',
			'Reduzieren Sie Ablenkungen: ruhiger Lernplatz, Handy weg, eine Aufgabe zur Zeit',
		],
	};
	return tips[type] || [];
}
